import { useEffect, useState } from 'react';
import { ToLocalStringConvert } from '../../Utils/toLocalStringConvert';

export interface LeaderboardRow {
  rank: string;
  name: string;
  store: string;
  ring: string;
  total_price: string;
  targeted_price: string;
  price_diff: string;
  price_diff_per: string;
  total_duration: string;
}

type ApiValue = number | string | null | undefined;

const hasValue = (value: ApiValue) => value !== null && value !== undefined && value !== '';

const toPrice = (value: ApiValue) => {
  if (!hasValue(value)) return '-';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return `${amount < 0 ? '-' : ''}$${ToLocalStringConvert(Math.abs(amount))}`;
};

const toPercent = (value: ApiValue) => {
  if (!hasValue(value)) return '-';
  const percent = Number(value);
  return Number.isFinite(percent) ? `${percent.toFixed(2)}%` : '-';
};

const toDuration = (value: ApiValue) => {
  if (!hasValue(value)) return '-';
  const seconds = Number(value);
  return Number.isFinite(seconds) ? `${Math.round(seconds)}s` : '-';
};

const toRow = (entry: Record<string, string | number | null>, index: number): LeaderboardRow => ({
  rank:           `${entry.rank ?? index + 1}`,
  name:           `${entry.name ?? '-'}`,
  store:          `${entry.store ?? '-'}`,
  ring:           `${entry.ring ?? '-'}`,
  total_price:    hasValue(entry.total_price) ? toPrice(entry.total_price) : '',
  targeted_price: toPrice(entry.targeted_price),
  price_diff:     toPrice(entry.price_diff),
  price_diff_per: toPercent(entry.price_diff_per),
  total_duration: toDuration(entry.total_duration),
});

const useLeaderboard = (enabled: boolean) => {
  const [ rows, setRows ] = useState<LeaderboardRow[]>([]);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ error, setError ] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();

    const getLeaderboardData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_SAVE_DESIGN}/dashboard`, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const { data } = await response.json();
        setRows((data ?? []).map(toRow));
        setError(false);
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          console.error(e);
          setError(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    getLeaderboardData();
    return () => {
      controller.abort();
    };
  }, [ enabled ]);

  return { rows, isLoading, error };
};

export default useLeaderboard;
